import { useState } from 'react'
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  RowSelectionState,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  RotateCcw,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Fuel,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  DataTableColumnHeader,
  DataTableFacetedFilter,
  DataTablePagination,
  DataTableToolbar,
} from '@/components/data-table'
import type {
  FuelTransactionTableRow,
  FuelTransactionStatus,
  FuelType,
  FuelTransactionSource,
} from '@/types/fuel-transactions'
import {
  FUEL_STATUS_LABELS,
  FUEL_STATUSES,
  FUEL_STATUS_VARIANTS,
  FUEL_TYPE_LABELS,
  FUEL_TYPES,
  FUEL_SOURCE_LABELS,
  FUEL_SOURCES,
  FUEL_SOURCE_VARIANTS,
  formatFuelQuantity,
  formatPricePerGallon,
  formatFuelTotal,
  formatFueledAt,
} from '@/types/fuel-transactions'

interface FuelTransactionsTableProps {
  data: FuelTransactionTableRow[]
  isLoading: boolean
  onEdit: (id: string) => void
  onDelete: (id: string, transactionNumber: string) => void
  onRestore?: (id: string) => void
  onImportCsv?: () => void
}

/** Icon mapping for fuel transaction status */
const statusIcons: Record<FuelTransactionStatus, React.ComponentType<{ className?: string }>> = {
  pending: Clock,
  completed: CheckCircle,
  voided: XCircle,
  disputed: AlertTriangle,
}

/** Status filter options with icons */
const statusFilterOptions = FUEL_STATUSES.map((status) => ({
  label: FUEL_STATUS_LABELS[status],
  value: status,
  icon: statusIcons[status],
}))

/** Fuel type filter options */
const fuelTypeFilterOptions = FUEL_TYPES.map((type) => ({
  label: FUEL_TYPE_LABELS[type],
  value: type,
  icon: Fuel,
}))

/** Source filter options */
const sourceFilterOptions = FUEL_SOURCES.map((source) => ({
  label: FUEL_SOURCE_LABELS[source],
  value: source,
}))

export function FuelTransactionsTable({
  data,
  isLoading,
  onEdit,
  onDelete,
  onRestore,
  onImportCsv,
}: FuelTransactionsTableProps) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'fueled_at', desc: true },
  ])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    source: false,
    meter_start: false,
    meter_end: false,
  })
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [globalFilter, setGlobalFilter] = useState('')

  const columns: ColumnDef<FuelTransactionTableRow>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'transaction_number',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Transaction #" />
      ),
      cell: ({ row }) => (
        <span className={cn('font-mono font-medium', row.original.deleted_at && 'line-through text-muted-foreground')}>
          {row.getValue('transaction_number')}
        </span>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const status = row.getValue('status') as FuelTransactionStatus
        return (
          <Badge variant={FUEL_STATUS_VARIANTS[status]}>
            {FUEL_STATUS_LABELS[status]}
          </Badge>
        )
      },
      filterFn: (row, id, value: string[]) => {
        if (!value || value.length === 0) return true
        return value.includes(row.getValue(id))
      },
    },
    {
      accessorKey: 'fueled_at',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Date/Time" />
      ),
      cell: ({ row }) => {
        const fueledAt = row.getValue('fueled_at') as string
        return <span className="text-sm">{formatFueledAt(fueledAt)}</span>
      },
    },
    {
      id: 'client',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Client" />
      ),
      cell: ({ row }) => {
        const clientName = row.original.client_name
        return clientName ? (
          <span className="font-medium">{clientName}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      },
    },
    {
      id: 'aircraft',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Aircraft" />
      ),
      cell: ({ row }) => {
        const tailNumber = row.original.tail_number
        const typeName = row.original.aircraft_type_name

        if (tailNumber) {
          return (
            <div>
              <div className="font-mono font-medium">{tailNumber}</div>
              {typeName && (
                <div className="text-xs text-muted-foreground">{typeName}</div>
              )}
            </div>
          )
        }

        return <span className="text-muted-foreground">-</span>
      },
    },
    {
      accessorKey: 'fuel_type',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Fuel" />
      ),
      cell: ({ row }) => {
        const fuelType = row.getValue('fuel_type') as FuelType
        return (
          <Badge variant="outline">
            {FUEL_TYPE_LABELS[fuelType]}
          </Badge>
        )
      },
      filterFn: (row, id, value: string[]) => {
        if (!value || value.length === 0) return true
        return value.includes(row.getValue(id))
      },
    },
    {
      accessorKey: 'quantity_gallons',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Quantity" />
      ),
      cell: ({ row }) => {
        const gallons = row.getValue('quantity_gallons') as number
        return (
          <span className="font-mono text-sm">
            {formatFuelQuantity(gallons)}
          </span>
        )
      },
    },
    {
      accessorKey: 'price_per_gallon',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Price/Gal" />
      ),
      cell: ({ row }) => {
        const price = row.getValue('price_per_gallon') as number
        return (
          <span className="font-mono text-sm">
            {formatPricePerGallon(price)}
          </span>
        )
      },
    },
    {
      accessorKey: 'total_amount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Total" />
      ),
      cell: ({ row }) => {
        const total = row.getValue('total_amount') as number
        return (
          <span className="font-mono font-medium">
            {formatFuelTotal(total)}
          </span>
        )
      },
    },
    {
      accessorKey: 'source',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Source" />
      ),
      cell: ({ row }) => {
        const source = row.getValue('source') as FuelTransactionSource
        return (
          <Badge variant={FUEL_SOURCE_VARIANTS[source]}>
            {FUEL_SOURCE_LABELS[source]}
          </Badge>
        )
      },
      filterFn: (row, id, value: string[]) => {
        if (!value || value.length === 0) return true
        return value.includes(row.getValue(id))
      },
    },
    {
      id: 'contract',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Contract / Stay" />
      ),
      cell: ({ row }) => {
        const contractNumber = row.original.contract_number
        const bookingNumber = row.original.booking_number
        if (contractNumber) return <span className="font-mono text-sm">{contractNumber}</span>
        if (bookingNumber) return <span className="font-mono text-sm">{bookingNumber}</span>
        return <span className="text-muted-foreground text-sm">-</span>
      },
    },
    {
      id: 'fueler',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Fueler" />
      ),
      cell: ({ row }) => {
        const fuelerName = row.original.fueler_name
        const truckId = row.original.truck_id

        if (fuelerName || truckId) {
          return (
            <div>
              {fuelerName && (
                <div className="text-sm">{fuelerName}</div>
              )}
              {truckId && (
                <div className="text-xs text-muted-foreground">
                  Truck: {truckId}
                </div>
              )}
            </div>
          )
        }

        return <span className="text-muted-foreground">-</span>
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const isDeleted = !!row.original.deleted_at
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isDeleted ? (
                <DropdownMenuItem onClick={() => onRestore?.(row.original.id)}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Restore
                </DropdownMenuItem>
              ) : (
                <>
                  <DropdownMenuItem onClick={() => onEdit(row.original.id)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      onDelete(row.original.id, row.original.transaction_number)
                    }
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
      enableHiding: false,
    },
  ]

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const search = filterValue.toLowerCase()
      const transactionNumber = (row.original.transaction_number || '').toLowerCase()
      const clientName = (row.original.client_name || '').toLowerCase()
      const tailNumber = (row.original.tail_number || '').toLowerCase()
      const contractNumber = (row.original.contract_number || '').toLowerCase()
      const bookingNumber = (row.original.booking_number || '').toLowerCase()
      const fuelerName = (row.original.fueler_name || '').toLowerCase()

      return (
        transactionNumber.includes(search) ||
        clientName.includes(search) ||
        tailNumber.includes(search) ||
        contractNumber.includes(search) ||
        bookingNumber.includes(search) ||
        fuelerName.includes(search)
      )
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 15,
      },
    },
  })

  // Export to CSV
  const handleExport = () => {
    const headers = [
      'Transaction #',
      'Status',
      'Date/Time',
      'Client',
      'Tail Number',
      'Aircraft Type',
      'Fuel Type',
      'Quantity (gal)',
      'Price/Gal',
      'Total',
      'Source',
      'Contract',
      'Booking #',
      'Fueler',
      'Truck',
      'Notes',
    ]
    const rows = table.getFilteredRowModel().rows.map((row) => [
      row.original.transaction_number,
      FUEL_STATUS_LABELS[row.original.status],
      row.original.fueled_at,
      row.original.client_name || '',
      row.original.tail_number || '',
      row.original.aircraft_type_name || '',
      FUEL_TYPE_LABELS[row.original.fuel_type],
      row.original.quantity_gallons.toString(),
      row.original.price_per_gallon.toString(),
      row.original.total_amount.toString(),
      FUEL_SOURCE_LABELS[row.original.source],
      row.original.contract_number || '',
      row.original.booking_number || '',
      row.original.fueler_name || '',
      row.original.truck_id || '',
      row.original.notes || '',
    ])

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `fuel_transactions_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  return (
    <div className="space-y-4">
      {/* Toolbar with search, filters, and view options */}
      <DataTableToolbar
        table={table}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        filterPlaceholder="Search fuel transactions..."
        onImport={onImportCsv}
        importLabel="Import CSV"
        onExport={handleExport}
        exportLabel="Export CSV"
      >
        <DataTableFacetedFilter
          column={table.getColumn('status')}
          title="Status"
          options={statusFilterOptions}
        />
        <DataTableFacetedFilter
          column={table.getColumn('fuel_type')}
          title="Fuel Type"
          options={fuelTypeFilterOptions}
        />
        <DataTableFacetedFilter
          column={table.getColumn('source')}
          title="Source"
          options={sourceFilterOptions}
        />
      </DataTableToolbar>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    Loading fuel transactions...
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={cn(!!row.original.deleted_at && 'opacity-50')}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    No fuel transactions found. Add a fuel transaction to get
                    started.
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <DataTablePagination table={table} />
    </div>
  )
}
